import { Loader } from "@googlemaps/js-api-loader";
import {
	Component,
	Element,
	Prop,
	Watch,
	getAssetPath,
	h,
} from "@stencil/core";

export type Position = {
	lat: number;
	lng: number;
};

@Component({
	tag: "google-map",
	styleUrl: "google-map.scss",
	shadow: true,
})
export class MapComponent {
	@Element() el: HTMLElement;

	@Prop() position: Position = { lat: -34.397, lng: 150.644 };
	@Prop() siteType = 8;

	map: google.maps.Map;
	marker: google.maps.Marker;
	streetViewLibrary: google.maps.StreetViewLibrary;
	streeViewService: google.maps.StreetViewService;

	@Watch("position")
	positionChanged(newValue: Position, oldValue: Position) {
		if (newValue.lat !== oldValue.lat || newValue.lng !== oldValue.lng) {
			this.updateMap();
		}
	}

	render() {
		return <div id="map" />;
	}

	async componentDidLoad() {
		const loader = new Loader({
			apiKey: process.env.GOOGLE_MAPS_API_KEY,
			version: "weekly",
		});

		this.streetViewLibrary = await loader.importLibrary("streetView");
		this.streeViewService = new this.streetViewLibrary.StreetViewService();

		const mapEl = this.el.shadowRoot.getElementById("map");

		this.map = new google.maps.Map(mapEl, {
			center: this.position,
			zoom: 15,
			mapTypeId: google.maps.MapTypeId.SATELLITE,
			tilt: 0,
			gestureHandling: "greedy",
			streetView: new this.streetViewLibrary.StreetViewPanorama(mapEl, {
				position: this.position,
			}),
		});

		// create marker
		this.marker = new google.maps.Marker({
			position: this.position,
			map: this.map,
			icon: {
				url: this.getMarkerIcon(this.siteType),
			},
		});

		// update map
		this.updateMap();
	}

	private updateMap = async () => {
		const request: google.maps.StreetViewLocationRequest = {
			location: this.position,
			radius: 250,
			preference: google.maps.StreetViewPreference.NEAREST,
			source: google.maps.StreetViewSource.OUTDOOR,
		};

		const { data, status } = await this.getPanoramaAsync(request);
		const streetViewAvailable = status === "OK";

		const streetView = this.map.getStreetView();

		if (streetViewAvailable) {
			streetView.setPano(data?.location.pano);
			streetView.setPov({
				heading: google.maps.geometry.spherical.computeHeading(
					data.location.latLng,
					this.position,
				),
				pitch: 0,
			});
		} else {
			this.map.setCenter(this.position);
			this.map.setZoom(16);
		}

		this.marker.setMap(streetViewAvailable ? streetView : this.map);
		streetView.setVisible(streetViewAvailable);
		this.updateMarkers();
	};

	private getPanoramaAsync(
		request: google.maps.StreetViewLocationRequest,
	): Promise<{
		data: google.maps.StreetViewPanoramaData;
		status: google.maps.StreetViewStatus;
	}> {
		return new Promise((resolve) => {
			this.streeViewService.getPanorama(request, (data, status) => {
				resolve({ data, status });
			});
		});
	}

	private updateMarkers() {
		this.marker.setPosition(this.position);
		this.marker.setIcon({
			url: this.getMarkerIcon(this.siteType),
		});
	}

	// get marker icon
	private getMarkerIcon = (type: number) => {
		switch (type) {
			case 8:
				return getAssetPath("assets/streetasset_icon.svg");
			case 14:
				return getAssetPath("assets/greenfield_icon.svg");
			case 15:
				return getAssetPath("assets/rooftop_icon.svg");
			case 38:
				return getAssetPath("assets/tower_icon.svg");
			default:
				return "https://maps.google.com/mapfiles/ms/icons/red-dot.png";
		}
	};
}
